import { createReducer, on } from '@ngrx/store';
import {
  box,
  Boxed,
  createFormGroupState,
  FormGroupState,
  onNgrxForms,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import { maxLength, required } from 'ngrx-forms/validation';
import {
  clearGlobalSettingsValueRecognition,
  clearMandatoryCheckTemplateForm,
  clearPeerPoolDisciplineForm,
  returnSavedGlobalSettingsValueRecognition,
  setAllMandatoryChecksTemplates,
  setAllPeerPoolDisciplines,
  setAllPhases,
  setGlobalSettingsValueRecognition,
  setMandatoryCheckTemplateForm,
  setPeerPoolDisciplineForm,
  setAllDADeliverablesTemplates,
  setDADeliverableTemplateForm,
  clearDADeliverableTemplateForm,
  setSMEDisciplineConfigCodesSuccess,
  clearBusinessUnitForm,
  setBusinessUnitForm,
  setAllBusinessUnits,
  setAssignments,
  setAllFocusAreaConfigs,
  setFocusAreaConfigForm,
  clearFocusAreaConfigForm,
} from './global-settings.actions';
import {
  AssignmentViewModel,
  BusinessUnitViewModel,
  DADeliverableTemplateViewModel,
  FocusAreaConfigViewModel,
  GlobalSettingsValueRecognitionViewModel,
  MandatoryCheckTemplateViewModel,
  SMEDisciplineConfigViewModel,
} from 'src/app/shared/models/autogenerated';
import { ValueRecognitionFormPhase } from '../../common/models/value-recognition-form-phase.model';
import { ValueRecognitionForm } from '../../common/models/value-recognition-form.model';
import { Constants } from 'src/app/shared/utils/constants';

export interface BusinessUnitForm {
  name: string | null;
  code: string | null;
  region: string | null;
}

const validateBusinessUnitForm = updateGroup<BusinessUnitForm>({
  name: validate(required),
  code: validate(required),
  region: validate(required),
});

const BUSINESS_UNIT_FORM_ID = 'ADAP_BUSINESS_UNIT_FORM';
const initialBusinessUnitValidateFormState =
  createFormGroupState<BusinessUnitForm>(BUSINESS_UNIT_FORM_ID, {
    name: null,
    code: null,
    region: null,
  });

export interface MandatoryCheckTemplateForm {
  owner: string | null;
  scopeOfReview: string | null;
  relevantPhases: Boxed<string[]>;
}

const validateMandatoryCheckTemplateForm =
  updateGroup<MandatoryCheckTemplateForm>({
    owner: validate(required),
    scopeOfReview: validate(required),
    relevantPhases: validate(required),
  });

const MANDATORY_CHECK_TEMPLATE_FORM_ID = 'ADAP_MANDATORY_CHECK_TEMPLATE__FORM';
const initialMandatoryCheckTemplateValidateFormState =
  createFormGroupState<MandatoryCheckTemplateForm>(
    MANDATORY_CHECK_TEMPLATE_FORM_ID,
    {
      owner: null,
      scopeOfReview: null,
      relevantPhases: box([]),
    }
  );

export interface DADeliverableTemplateForm {
  id?: string | null;
  onshore: boolean;
  offshore: boolean;
  title: string | null;
  definition?: string | null;
  deliverableDiscipline: string | null;
  docType?: string | null;
  deliverableId?: string | null;
  drsCode?: string | null;
  daStage?: string | null;
  origin: string;
  phaseGuidelines: Boxed<string[]>;
  DADeliverablePhaseGuidelines: any[];
}

const validateDADeliverableTemplateForm =
  updateGroup<DADeliverableTemplateForm>({
    title: validate(required),
    deliverableDiscipline: validate(required),
    docType: validate(required),
    drsCode: validate(required),
    phaseGuidelines: validate(required),
  });

const DA_DELIVERABLE_TEMPLATE_FORM_ID = 'ADAP_DA_DELIVERABLE_TEMPLATE_FORM';
const initialDADeliverableTemplateFormState =
  createFormGroupState<DADeliverableTemplateForm>(
    DA_DELIVERABLE_TEMPLATE_FORM_ID,
    {
      id: Constants.GUID_DEFAULT_VALUE,
      onshore: false,
      offshore: false,
      title: null,
      definition: null,
      deliverableDiscipline: null,
      docType: null,
      deliverableId: null,
      drsCode: null,
      daStage: null,
      origin: '',
      phaseGuidelines: box([]),
      DADeliverablePhaseGuidelines: [
        {
          projectPhaseTemplate: {
            phaseId: '',
          },
          deliverableCategory: '',
          typicallyNOJVProvided: false,
          typicallyDASampled: false,
          daDeliverableTemplateId: Constants.GUID_DEFAULT_VALUE,
          daDeliverableTemplate: {
            origin: '',
            DADeliverablePhaseGuidelines: [],
          },
          workloadEstimates: [],
        },
      ],
    }
  );

export interface FocusAreaConfigForm {
  id?: string | null;
  hideIfOnShore: boolean;
  hideIfOffShore: boolean;
  title: string | null;
  phase2BDefinition: string | undefined;
  phase2CDefinition: string | undefined;
  phase3Definition: string | undefined;
  phase4Definition: string | undefined;
}

const validateFocusAreaConfigForm = updateGroup<FocusAreaConfigForm>({
  title: validate(required),
});

const FOCUS_AREA_CONFIG_FORM_ID = 'ADAP_FOCUS_AREA_CONFIG_FORM';
const initialFocusAreaConfigFormState =
  createFormGroupState<FocusAreaConfigForm>(FOCUS_AREA_CONFIG_FORM_ID, {
    id: Constants.GUID_DEFAULT_VALUE,
    hideIfOnShore: false,
    hideIfOffShore: false,
    title: null,
    phase2BDefinition: undefined,
    phase2CDefinition: undefined,
    phase3Definition: undefined,
    phase4Definition: undefined,
  });

export interface PeerPoolDisciplineForm {
  id?: string | null;
  name: string | null;
  code: string | null;
  chapterManagers: Boxed<string[]>;
}

const validatePeerPoolDisciplineForm = updateGroup<PeerPoolDisciplineForm>({
  name: validate(required, maxLength(100)),
  code: validate(required, maxLength(3)),
});

const PEER_POOL_DISCIPLINE_FORM_ID = 'ADAP_PEER_POOL_DISCIPLINE_FORM';
const initialPeerPoolDisciplineFormState =
  createFormGroupState<PeerPoolDisciplineForm>(PEER_POOL_DISCIPLINE_FORM_ID, {
    id: null,
    name: null,
    code: null,
    chapterManagers: box([]),
  });
export interface GlobalSettingsDAEngineerState {
  globalSettingsValueRecognition:
    | GlobalSettingsValueRecognitionViewModel[]
    | null;
  globalSettingsValueRecognitionForm: FormGroupState<ValueRecognitionForm>;
  mandatoryChecksTemplates: MandatoryCheckTemplateViewModel[] | null;
  mandatoryCheckTemplateForm: FormGroupState<MandatoryCheckTemplateForm>;
  businessUnits: BusinessUnitViewModel[] | null;
  businessUnitForm: FormGroupState<BusinessUnitForm>;
  peerPoolDisciplines: SMEDisciplineConfigViewModel[] | null;
  peerPoolDisciplineForm: FormGroupState<PeerPoolDisciplineForm>;
  allPhases: string[];
  daDeliverableTemplateForm: FormGroupState<DADeliverableTemplateForm>;
  daDeliverableTemplates: DADeliverableTemplateViewModel[] | null;
  smeDisciplineConfigCodes: string[] | null;
  allAssignments: AssignmentViewModel[] | null;
  focusAreaConfigForm: FormGroupState<FocusAreaConfigForm>;
  focusAreaConfigs: FocusAreaConfigViewModel[] | null;
}

const GLOBAL_SETTINGS_DA_ENGINEER_FORM_ID =
  'ADAP_GLOBAL_DA_ENGINEER_SETTINGS_FORM';
const initialGlobalSettingsValueRecognitionFormState =
  createFormGroupState<ValueRecognitionForm>(
    GLOBAL_SETTINGS_DA_ENGINEER_FORM_ID,
    {
      phase2: {
        id: null,
        lossesLow: null,
        lossesMed: null,
        lossesHigh: null,
        mitigationLow: null,
        mitigationMed: null,
        mitigationHigh: null,
        clarification: null,
        lowUrgencyLowVulnerability: null,
        lowUrgencyMedVulnerability: null,
        lowUrgencyHighVulnerability: null,
        medUrgencyLowVulnerability: null,
        medUrgencyMedVulnerability: null,
        medUrgencyHighVulnerability: null,
        highUrgencyLowVulnerability: null,
        highUrgencyMedVulnerability: null,
        highUrgencyHighVulnerability: null,
      },
      phase3: {
        id: null,
        lossesLow: null,
        lossesMed: null,
        lossesHigh: null,
        mitigationLow: null,
        mitigationMed: null,
        mitigationHigh: null,
        clarification: null,
        lowUrgencyLowVulnerability: null,
        lowUrgencyMedVulnerability: null,
        lowUrgencyHighVulnerability: null,
        medUrgencyLowVulnerability: null,
        medUrgencyMedVulnerability: null,
        medUrgencyHighVulnerability: null,
        highUrgencyLowVulnerability: null,
        highUrgencyMedVulnerability: null,
        highUrgencyHighVulnerability: null,
      },
      phase4: {
        id: null,
        lossesLow: null,
        lossesMed: null,
        lossesHigh: null,
        mitigationLow: null,
        mitigationMed: null,
        mitigationHigh: null,
        clarification: null,
        lowUrgencyLowVulnerability: null,
        lowUrgencyMedVulnerability: null,
        lowUrgencyHighVulnerability: null,
        medUrgencyLowVulnerability: null,
        medUrgencyMedVulnerability: null,
        medUrgencyHighVulnerability: null,
        highUrgencyLowVulnerability: null,
        highUrgencyMedVulnerability: null,
        highUrgencyHighVulnerability: null,
      },
    }
  );

const initialState: GlobalSettingsDAEngineerState = {
  globalSettingsValueRecognition: null,
  globalSettingsValueRecognitionForm:
    initialGlobalSettingsValueRecognitionFormState,
  mandatoryChecksTemplates: null,
  mandatoryCheckTemplateForm: initialMandatoryCheckTemplateValidateFormState,
  businessUnits: null,
  businessUnitForm: initialBusinessUnitValidateFormState,
  peerPoolDisciplines: null,
  peerPoolDisciplineForm: initialPeerPoolDisciplineFormState,
  allPhases: [],
  daDeliverableTemplateForm: initialDADeliverableTemplateFormState,
  daDeliverableTemplates: null,
  smeDisciplineConfigCodes: null,
  allAssignments: null,
  focusAreaConfigForm: initialFocusAreaConfigFormState,
  focusAreaConfigs: null,
};

const validateGlobalSettingsForm = updateGroup<ValueRecognitionForm>({
  phase2: updateGroup<ValueRecognitionFormPhase>({
    id: validate(required),
    lossesLow: validate(required),
    lossesMed: validate(required),
    lossesHigh: validate(required),
    mitigationLow: validate(required),
    mitigationMed: validate(required),
    mitigationHigh: validate(required),
    clarification: validate(required),
    lowUrgencyLowVulnerability: validate(required),
    lowUrgencyMedVulnerability: validate(required),
    lowUrgencyHighVulnerability: validate(required),
    medUrgencyLowVulnerability: validate(required),
    medUrgencyMedVulnerability: validate(required),
    medUrgencyHighVulnerability: validate(required),
    highUrgencyLowVulnerability: validate(required),
    highUrgencyMedVulnerability: validate(required),
    highUrgencyHighVulnerability: validate(required),
  }),
  phase3: updateGroup<ValueRecognitionFormPhase>({
    id: validate(required),
    lossesLow: validate(required),
    lossesMed: validate(required),
    lossesHigh: validate(required),
    mitigationLow: validate(required),
    mitigationMed: validate(required),
    mitigationHigh: validate(required),
    clarification: validate(required),
    lowUrgencyLowVulnerability: validate(required),
    lowUrgencyMedVulnerability: validate(required),
    lowUrgencyHighVulnerability: validate(required),
    medUrgencyLowVulnerability: validate(required),
    medUrgencyMedVulnerability: validate(required),
    medUrgencyHighVulnerability: validate(required),
    highUrgencyLowVulnerability: validate(required),
    highUrgencyMedVulnerability: validate(required),
    highUrgencyHighVulnerability: validate(required),
  }),
  phase4: updateGroup<ValueRecognitionFormPhase>({
    id: validate(required),
    lossesLow: validate(required),
    lossesMed: validate(required),
    lossesHigh: validate(required),
    mitigationLow: validate(required),
    mitigationMed: validate(required),
    mitigationHigh: validate(required),
    clarification: validate(required),
    lowUrgencyLowVulnerability: validate(required),
    lowUrgencyMedVulnerability: validate(required),
    lowUrgencyHighVulnerability: validate(required),
    medUrgencyLowVulnerability: validate(required),
    medUrgencyMedVulnerability: validate(required),
    medUrgencyHighVulnerability: validate(required),
    highUrgencyLowVulnerability: validate(required),
    highUrgencyMedVulnerability: validate(required),
    highUrgencyHighVulnerability: validate(required),
  }),
});

function createValueRecognitionForm(
  globalSettingsValueRecognition: GlobalSettingsValueRecognitionViewModel[]
) {
  return createFormGroupState<ValueRecognitionForm>(
    GLOBAL_SETTINGS_DA_ENGINEER_FORM_ID,
    {
      phase2:
        globalSettingsValueRecognition.find((item) => item.phaseNumber === 2) ||
        initialGlobalSettingsValueRecognitionFormState.value.phase2,
      phase3:
        globalSettingsValueRecognition.find((item) => item.phaseNumber === 3) ||
        initialGlobalSettingsValueRecognitionFormState.value.phase3,
      phase4:
        globalSettingsValueRecognition.find((item) => item.phaseNumber === 4) ||
        initialGlobalSettingsValueRecognitionFormState.value.phase4,
    }
  );
}

const rawGlobalSettingsReducer = createReducer(
  initialState,
  onNgrxForms(),
  on(
    setGlobalSettingsValueRecognition,
    (state, { globalSettingsValueRecognition }) => ({
      ...state,
      globalSettingsValueRecognition,
      globalSettingsValueRecognitionForm: createValueRecognitionForm(
        globalSettingsValueRecognition
      ),
    })
  ),
  on(clearGlobalSettingsValueRecognition, (state) => ({
    ...state,
    globalSettingsValueRecognition: null,
    globalSettingsValueRecognitionForm:
      initialGlobalSettingsValueRecognitionFormState,
  })),
  on(returnSavedGlobalSettingsValueRecognition, (state) => ({
    ...state,
    globalSettingsValueRecognitionForm: createValueRecognitionForm(
      state.globalSettingsValueRecognition!
    ),
  })),
  on(setAllMandatoryChecksTemplates, (state, { mandatoryChecksTemplates }) => ({
    ...state,
    mandatoryChecksTemplates,
  })),
  on(setMandatoryCheckTemplateForm, (state, { mandatoryCheckTemplate }) => ({
    ...state,
    mandatoryCheckTemplateForm:
      createFormGroupState<MandatoryCheckTemplateForm>(
        MANDATORY_CHECK_TEMPLATE_FORM_ID,
        {
          owner: mandatoryCheckTemplate.owner,
          scopeOfReview: mandatoryCheckTemplate.scopeOfReview,
          relevantPhases: box(mandatoryCheckTemplate.relevantPhases),
        }
      ),
  })),
  on(clearMandatoryCheckTemplateForm, (state) => ({
    ...state,
    mandatoryCheckTemplateForm: initialMandatoryCheckTemplateValidateFormState,
  })),
  on(setAllBusinessUnits, (state, { businessUnits }) => ({
    ...state,
    businessUnits,
  })),
  on(setBusinessUnitForm, (state, { businessUnit }) => ({
    ...state,
    businessUnitForm: createFormGroupState<BusinessUnitForm>(
      BUSINESS_UNIT_FORM_ID,
      {
        name: businessUnit.name,
        code: businessUnit.code,
        region: businessUnit.region,
      }
    ),
  })),
  on(clearBusinessUnitForm, (state) => ({
    ...state,
    businessUnitForm: initialBusinessUnitValidateFormState,
  })),
  on(setAllPhases, (state, { allPhases }) => ({
    ...state,
    allPhases,
  })),
  on(setAllPeerPoolDisciplines, (state, { peerPoolDisciplines }) => ({
    ...state,
    peerPoolDisciplines,
  })),
  on(clearPeerPoolDisciplineForm, (state) => ({
    ...state,
    currentPeerPoolDiscipline: null,
    peerPoolDisciplineForm: initialPeerPoolDisciplineFormState,
    isFormUpdated: false,
  })),
  on(setPeerPoolDisciplineForm, (state, { peerPoolDiscipline }) => ({
    ...state,
    peerPoolDisciplineForm: createFormGroupState<PeerPoolDisciplineForm>(
      PEER_POOL_DISCIPLINE_FORM_ID,
      {
        id: peerPoolDiscipline.id,
        name: peerPoolDiscipline.name,
        code: peerPoolDiscipline.code!,
        chapterManagers: box(peerPoolDiscipline.chapterManagers!),
      }
    ),
  })),
  on(setAllDADeliverablesTemplates, (state, { daDeliverablesTemplates }) => ({
    ...state,
    daDeliverableTemplates: daDeliverablesTemplates,
  })),
  on(setAllFocusAreaConfigs, (state, { focusAreaConfigs }) => ({
    ...state,
    focusAreaConfigs,
  })),
  on(setFocusAreaConfigForm, (state, { focusAreaConfig }) => ({
    ...state,
    focusAreaConfigForm: createFormGroupState<FocusAreaConfigForm>(
      FOCUS_AREA_CONFIG_FORM_ID,
      {
        id: focusAreaConfig.id,
        hideIfOnShore: focusAreaConfig.hideIfOnShore,
        hideIfOffShore: focusAreaConfig.hideIfOffShore,
        title: focusAreaConfig.title!,
        phase2BDefinition: focusAreaConfig.phase2BDefinition,
        phase2CDefinition: focusAreaConfig.phase2CDefinition,
        phase3Definition: focusAreaConfig.phase3Definition,
        phase4Definition: focusAreaConfig.phase4Definition,
      }
    ),
  })),
  on(setSMEDisciplineConfigCodesSuccess, (state, { codes }) => ({
    ...state,
    smeDisciplineConfigCodes: codes,
  })),
  on(setDADeliverableTemplateForm, (state, { daDeliverableTemplate }) => ({
    ...state,
    daDeliverableTemplateForm: createFormGroupState<DADeliverableTemplateForm>(
      DA_DELIVERABLE_TEMPLATE_FORM_ID,
      {
        id: daDeliverableTemplate.id,
        onshore: daDeliverableTemplate.onshore,
        offshore: daDeliverableTemplate.offshore,
        title: daDeliverableTemplate.title!,
        definition: daDeliverableTemplate.definition,
        deliverableDiscipline: daDeliverableTemplate.deliverableDiscipline!,
        docType: daDeliverableTemplate.docType,
        deliverableId: daDeliverableTemplate.deliverableId,
        drsCode: daDeliverableTemplate.drsCode,
        daStage: daDeliverableTemplate.daStage,
        origin: daDeliverableTemplate.origin,
        DADeliverablePhaseGuidelines:
          daDeliverableTemplate.daDeliverablePhaseGuidelines,
        phaseGuidelines: box(
          daDeliverableTemplate.daDeliverablePhaseGuidelines?.map(
            (guideline) => guideline.projectPhaseTemplate.phaseId
          )
        ),
      }
    ),
  })),
  on(clearDADeliverableTemplateForm, (state) => ({
    ...state,
    daDeliverableTemplateForm: initialDADeliverableTemplateFormState,
  })),
  on(setAssignments, (state, { assignments }) => {
    return {
      ...state,
      allAssignments: assignments,
    };
  }),
  on(clearFocusAreaConfigForm, (state) => ({
    ...state,
    focusAreaConfigForm: initialFocusAreaConfigFormState,
  }))
);

const daDeliverableTemplateReducer = wrapReducerWithFormStateUpdate(
  rawGlobalSettingsReducer,
  (s) => s.daDeliverableTemplateForm,
  validateDADeliverableTemplateForm
);

const businessUnitReducer = wrapReducerWithFormStateUpdate(
  daDeliverableTemplateReducer,
  (s) => s.businessUnitForm,
  validateBusinessUnitForm
);

const mandatoryCheckTemplateReducer = wrapReducerWithFormStateUpdate(
  businessUnitReducer,
  (s) => s.mandatoryCheckTemplateForm,
  validateMandatoryCheckTemplateForm
);

const peerPoolDisciplineReducer = wrapReducerWithFormStateUpdate(
  mandatoryCheckTemplateReducer,
  (s) => s.peerPoolDisciplineForm,
  validatePeerPoolDisciplineForm
);

const focusAreaConfigReducer = wrapReducerWithFormStateUpdate(
  peerPoolDisciplineReducer,
  (s) => s.focusAreaConfigForm,
  validateFocusAreaConfigForm
);

export const GlobalSettingsDAEngineerReducerWithForm =
  wrapReducerWithFormStateUpdate(
    focusAreaConfigReducer,
    (s) => s.globalSettingsValueRecognitionForm,
    validateGlobalSettingsForm
  );
